import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ReactComponent as PencilImage } from 'images/icons/pencil.svg';
import useCondensedInfo from 'typescript/apps/checkout_v3/components/condensed_info/use_condensed_info';
import CondensedInfo from 'typescript/apps/checkout_v3/components/condensed_info/condensed_info';
import PageTitle from 'typescript/apps/checkout_v3/components/page_title';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import PageLoader from 'typescript/components/page_loader';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { useListOffers } from 'typescript/hooks/use_offers_client';
import { useListPaymentOptions } from 'typescript/hooks/use_payment_options_client';
import { SelectAndRevealOptionType } from 'typescript/components/select_and_reveal/types';
import useSelectAndReveal from 'typescript/components/select_and_reveal/use_select_and_reveal';
import SelectAndReveal from 'typescript/components/select_and_reveal/select_and_reveal';
import {
  orderHasInstallments,
  buildSelectAndRevealOptions,
} from 'typescript/utilities/payment_options';
import { useIsProcessingAnyRequest } from 'typescript/hooks/use_react_query_utils';
import { CHECKOUT_ROUTES } from 'typescript/constants/enums/checkout';
import {
  CHECKOUT_PAGE_CLICK_EVENTS,
  COMPONENT_NAMES,
} from 'typescript/constants/tracking';
import {
  onCheckoutPageClick,
  onCheckoutPageViewCombined,
} from 'typescript/utilities/tracking';
import { usePaymentContext } from 'typescript/context/payment_context';

const Payment = () => {
  const { t } = useTranslation();

  const { orderNumber, trackingData } = useCheckoutFlow();

  const { submittingPayment } = usePaymentContext();

  const history = useHistory();

  const { isProcessing } = useIsProcessingAnyRequest();

  const { paymentOptionsData, isPending: paymentOptionsIsPending } =
    useListPaymentOptions({ orderNumber });

  const hasInstallments =
    !paymentOptionsIsPending &&
    orderHasInstallments(paymentOptionsData.payment_options);

  const {
    orderSummaryData,
    isPending: orderDataIsPending,
    // error: orderDataError, // TODO: handle error state
  } = useGetOrderSummary({ orderNumber });

  const { offersData, isPending: offersDataIsPending } = useListOffers({
    orderNumber,
    enabled: hasInstallments,
  });

  const offersPending = hasInstallments && offersDataIsPending;

  const condensedInfoDetails = useMemo(
    () => useCondensedInfo({ orderSummaryData }),
    [orderSummaryData],
  );

  const selectAndRevealOptions = useMemo(() => {
    if (orderDataIsPending || paymentOptionsIsPending || offersPending) {
      return [];
    }
    return buildSelectAndRevealOptions(
      paymentOptionsData.payment_options,
      offersData?.offers,
      orderSummaryData,
    );
  }, [orderSummaryData, paymentOptionsData, offersData]);

  const { selectOptions } = useSelectAndReveal({
    optionType: SelectAndRevealOptionType.PAYMENT,
    options: selectAndRevealOptions,
  });

  // TODO (bdang) : Migrate existing utils to redirect while maintaining params
  const redirectToInformation = () => {
    onCheckoutPageClick({
      eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.payment_edit_accessory,
      trackingData,
    });
    history.push(CHECKOUT_ROUTES.DELIVERY);
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    onCheckoutPageViewCombined({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT_V3.PAYMENT,
      additionalParameters: trackingData,
    });
  }, []);

  if (orderDataIsPending || paymentOptionsIsPending || offersPending) {
    return <PageLoader />;
  }

  return (
    <div className="shop-grid-container">
      <PageTitle
        className="shop-grid-item-two-third"
        title={t('checkout.delivery.title.pageHeader')}
        subtitle={t('checkout.delivery.subtitle.pageHeader')}
      />
      <CondensedInfo
        className="shop-grid-item-full"
        condensedInfoDetails={condensedInfoDetails}
        trailingAccessory={<PencilImage />}
        onTrailingAccessoryClick={redirectToInformation}
        disableTrailingAccessory={isProcessing || submittingPayment}
      />
      <PageTitle
        className="shop-grid-item-two-third"
        title={t('checkout.payment_v3.title.pageHeader')}
        subtitle={t('checkout.payment_v3.title.subtitle')}
      />
      <SelectAndReveal
        className="shop-grid-item-full"
        selectOptions={selectOptions}
        selectOptionCardDisabled={submittingPayment}
      />
    </div>
  );
};

export default Payment;
